import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useState } from "react";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";
import Skip from "@/assets/icons/authentication/Skip.svg";

import {
  prevStep,
  nextStep,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import ComfortBlock from "../../ComfortBlock";
import SkillsSection from "./components/SkillsSection";
import PortfolioVideosSection from "./components/PortfolioVideosSection";
import ProfileLinksSection, {
  validateProfileLinks,
} from "./components/ProfileLinksSection";
import { SkipToast } from "./Skiptoast";
import { scrollToFirstError } from "@/utils/formValidationV2";
import LanguagesSection, {
  validateLanguages,
} from "./components/LanguagesSection";
import { Check } from "lucide-react";
import { usePricingFormula } from "@/hooks/usePricingFormula";

type ApplicationStep4Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep4 = ({
  currentStep,
  setCurrentStep,
}: ApplicationStep4Props) => {
  const dispatch = useAppDispatch();
  const [showSkipToast, setShowSkipToast] = useState(false);
  const [forceValidate, setForceValidate] = useState(false);
  const { ucdT } = usePricingFormula();
  const isIndianCreator = ucdT?.country === "IN";

  const lastDrawnSalary = useAppSelector(
    (s) => s.signUpV2.v2Extras.lastDrawnSalary ?? "",
  );
  const expectedPayout = useAppSelector(
    (s) => s.signUpV2.v2Extras.expectedPayout ?? "",
  );
  const expectedPayoutType = useAppSelector(
    (s) => s.signUpV2.v2Extras.expectedPayoutType ?? "Per Month",
  );
  const comfortableWith = useAppSelector(
    (s) => s.signUpV2.v2Extras.comfortableWith,
  );
  const availabilityPerWeek = useAppSelector(
    (s) => s.signUpV2.v2Extras.availableHoursPerWeek,
  );
  const weekendAvailabilityV2 = useAppSelector(
    (s) => s.signUpV2.v2Extras.availabilityForWeekendsOrEvenings,
  );
  const avoidTopics = useAppSelector((s) => s.signUpV2.v2Extras.avoidTopics);
  const profileLinks =
    useAppSelector((s) => s.signUpV2.v2Extras.profileLinks) ?? [];
  const languages = useAppSelector((s) => s.signUpV2.v2Extras.languages) ?? [];
  const portfolioVideos =
    useAppSelector((s) => s.signUpV2.v2Extras.portfolioVideos) ?? [];
  const skills = useAppSelector((s) => s.signUpV2.v2Extras.skillsMulti) ?? [];

  const [topicOtherOpen, setTopicOtherOpen] = useState(false);
  const [topicOtherInput, setTopicOtherInput] = useState("");

  const setComfort = (key: string, selected: string) => {
    dispatch(
      updateV2Field("comfortableWith", {
        ...comfortableWith,
        [key]: selected,
      }),
    );
  };

  const toggleAvoidTopic = (topic: string) => {
    const exists = avoidTopics.includes(topic);
    const next = exists
      ? avoidTopics.filter((t) => t !== topic)
      : [...avoidTopics, topic];
    dispatch(updateV2Field("avoidTopics", next));
  };

  const validateStep4 = () => {
    const validationErrors: Record<string, string> = {};

    // skills required
    if (!skills.length) {
      validationErrors.skills = "This field is required";
    }

    // profile links required + existing custom validator
    if (Object.keys(validateProfileLinks(profileLinks)).length) {
      validationErrors.profileLinks = "Add at least one profile link";
    }

    // all comfortableWith options required
    if (
      !comfortableWith?.postFromOwnAccount ||
      !comfortableWith?.runAds ||
      !comfortableWith?.createBrandAccount
    ) {
      validationErrors.comfortableWith = "Please answer all options";
    }

    // portfolio videos required
    if (!portfolioVideos.length) {
      validationErrors.portfolioVideos = "This field is required";
    }

    // languages required + existing custom validator
    if (!languages.length) {
      validationErrors.languages = "This field is required";
    } else {
      const languageErrors = validateLanguages(languages, isIndianCreator);
      if (languageErrors.languageFluency) {
        validationErrors.languages = "Please select fluency for English";
      }
    }

    // availability required
    if (!availabilityPerWeek) {
      validationErrors.availabilityPerWeek = "This field is required";
    }
    // Expected payout required
    if (!expectedPayout) {
      validationErrors.expectedPayout = "This field is required";
    }
    // Expected payout required
    if (!lastDrawnSalary) {
      validationErrors.lastDrawnSalary = "This field is required";
    }

    return validationErrors;
  };

  // Keep feedback current as users correct fields after attempting to proceed.
  const errors = forceValidate ? validateStep4() : {};
  const fieldLabels: Record<string, string> = {
    skills: "Skills",
    profileLinks: "Profile links",
    comfortableWith: "Creator add-ons & permissions",
    portfolioVideos: "Portfolio videos",
    languages: "Languages",
    availabilityPerWeek: "Weekly availability",
    lastDrawnSalary: "Last drawn salary",
    expectedPayout: "Expected payout",
  };

  const handleProceed = () => {
    setForceValidate(true);

    const validationErrors = validateStep4();

    if (Object.keys(validationErrors).length > 0) {
      requestAnimationFrame(() => scrollToFirstError(validationErrors));
      return;
    }

    dispatch(nextStep());
    setCurrentStep((prev) => prev + 1);
  };

  return (
    <>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-x-2 whitespace-nowrap">
            <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
              <p className="text-white text-[11px] leading-5 tracking-normal">
                Step 4 of 5
              </p>
            </div>
            <h1 className="text-[#060B13] text-lg md:text-xl tracking-[-0.7px] font-medium">
              Build Your Creator Portfolio
            </h1>
          </div>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
            This is the profile brands will see when shortlisting creators. Add
            what shows your experience best.
          </p>
        </div>

        <div className="space-y-8">
          <div id="skills">
            <SkillsSection />
            {errors.skills && (
              <p className="text-red-500 text-xs mt-1">{errors.skills}</p>
            )}
          </div>

          <div id="profileLinks">
            <ProfileLinksSection forceValidate={forceValidate} />
            {errors.profileLinks && (
              <p className="text-red-500 text-xs mt-1">{errors.profileLinks}</p>
            )}
          </div>

          <div id="comfortableWith">
            <div>
              <h6 className="text-[#121217] text-sm tracking-normal font-medium">
                Creator Add-ons & Permissions
              </h6>
              <p className="text-[#475467] text-[10px] tracking-normal mt-[2px]">
                Some brands may need extra support beyond content creation.
                Select what you’re open to offering.
              </p>
            </div>

            <div className="w-full space-y-2 mt-4">
              <label className="text-xs font-medium text-[#121217] tracking-[-1.5%]">
                What are you comfortable with?
              </label>

              <div className="space-y-3">
                <ComfortBlock
                  title="Post content from your own account"
                  subDescription="Includes organic posts or collab posts."
                  options={["Yes, I can post from my account", "Not available"]}
                  value={comfortableWith.postFromOwnAccount || ""}
                  onChange={(val: string) =>
                    setComfort("postFromOwnAccount", val)
                  }
                />

                <ComfortBlock
                  title="Run ads via your account (Collab / Branded Ads)"
                  subDescription="Only for brand-approved, paid collaborations."
                  options={["Yes, I’m open to running ads", "Not available"]}
                  value={comfortableWith.runAds || ""}
                  onChange={(val: string) => setComfort("runAds", val)}
                />

                <ComfortBlock
                  title="Create a new account for a brand"
                  subDescription="For brands that need a fresh account for campaigns."
                  options={["Yes, I can set up an account", "Not available"]}
                  value={comfortableWith.createBrandAccount || ""}
                  onChange={(val: string) =>
                    setComfort("createBrandAccount", val)
                  }
                />
              </div>

              {errors.comfortableWith && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.comfortableWith}
                </p>
              )}
            </div>
          </div>

          <div id="portfolioVideos">
            <PortfolioVideosSection />
            {errors.portfolioVideos && (
              <p className="text-red-500 text-xs mt-1">
                {errors.portfolioVideos}
              </p>
            )}
          </div>

          <div>
            <LanguagesSection forceValidate={forceValidate} />
            {errors.languages && (
              <p className="text-red-500 text-xs mt-1">{errors.languages}</p>
            )}
          </div>

          <div id="availabilityPerWeek">
            <p className="tracking-normal text-sm text-[#121217] font-medium">
              Availability
            </p>
            <p className="mt-[2px] text-[10px] tracking-normal">
              This helps us prioritise you for urgent or time-sensitive
              projects.
            </p>

            <div className="bg-[#FAFAFA] py-3 px-2 rounded-lg mt-2">
              <p className="text-[#121217] text-xs tracking-normal">
                Weekly availability
              </p>
              <div className="flex flex-wrap mt-2 gap-2">
                {[
                  "1–5 hrs / week",
                  "6–10 hrs / week",
                  "11–20 hrs / week",
                  "20+ hrs / week",
                ].map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      dispatch(updateV2Field("availableHoursPerWeek", label));
                    }}
                    className={`px-1.5 py-1 border rounded-lg bg-white text-xs tracking-[-0.5px]
                      ${
                        availabilityPerWeek === label
                          ? "border-[#FF6600] bg-[#FFF4EC]"
                          : "border-[#D8D8DD]"
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {errors.availabilityPerWeek && (
                <p className="text-red-500 text-xs mt-2">
                  {errors.availabilityPerWeek}
                </p>
              )}

              <div className="mt-6">
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <button
                    type="button"
                    onClick={() =>
                      dispatch(
                        updateV2Field(
                          "availabilityForWeekendsOrEvenings",
                          !weekendAvailabilityV2,
                        ),
                      )
                    }
                    className={`relative flex size-3.5 items-center justify-center rounded border transition
        ${
          weekendAvailabilityV2
            ? "border-orange-500 bg-orange-500"
            : "border-[#D0D5DD] bg-white"
        }`}
                    aria-pressed={weekendAvailabilityV2}
                    aria-label="Flexible timings"
                  >
                    {weekendAvailabilityV2 && (
                      <Check size={10} strokeWidth={3} className="text-white" />
                    )}
                  </button>

                  <input
                    type="checkbox"
                    checked={weekendAvailabilityV2}
                    onChange={(e) =>
                      dispatch(
                        updateV2Field(
                          "availabilityForWeekendsOrEvenings",
                          e.target.checked,
                        ),
                      )
                    }
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                  />

                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="font-medium text-[#121217]">
                      Flexible timings
                    </span>
                    <span className="text-[#475467]">
                      Open to working on weekends or evenings?
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Compensation Preferences */}
          <div className="mt-6">
            <h3 className="text-[#121217] text-sm font-medium tracking-normal">
              Compensation Preferences
            </h3>

            <p className="mt-1 text-[#667085] text-[9px]">
              Please Note - We generally follow standardized pricing on the
              platform. The details below help us benchmark and may be used for
              internal or special projects where pricing is discussed.
            </p>

            <div className="mt-2 space-y-2 rounded-[8px] bg-[#F8F8F8] p-2">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <label className="shrink-0 text-[#475467] text-[12px] font-medium">
                  Last drawn salary
                </label>

                <input
                  type="text"
                  id="lastDrawnSalary"
                  aria-invalid={!!errors.lastDrawnSalary}
                  value={lastDrawnSalary}
                  onChange={(e) =>
                    dispatch(updateV2Field("lastDrawnSalary", e.target.value))
                  }
                  placeholder="e.g. ₹4000"
                  className="w-full h-[30px] rounded-[8px] border border-[#EDEDED] bg-white px-2 py-0.5 text-[11px] text-[#121217] outline-none placeholder:text-[#959FB2] focus:border-[#D0D5DD]"
                />
              </div>

              {errors.lastDrawnSalary && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.lastDrawnSalary}
                </p>
              )}

              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <label className="shrink-0 text-[#475467] text-[12px] font-medium">
                  Expected payout
                </label>

                <div className="flex flex-col sm:flex-row w-full gap-2">
                  <input
                    type="text"
                    id="expectedPayout"
                    aria-invalid={!!errors.expectedPayout}
                    value={expectedPayout}
                    onChange={(e) =>
                      dispatch(updateV2Field("expectedPayout", e.target.value))
                    }
                    placeholder="Per video or per month (e.g. ₹4,000 / video)"
                    className="w-full h-[30px] rounded-[8px] border border-[#E4E7EC] bg-white px-2 text-[11px] text-[#121217] outline-none placeholder:text-[#98A2B3] focus:border-[#D0D5DD] focus:ring-1 focus:ring-[#D0D5DD]"
                  />

                  <div className="relative w-full sm:w-[150px]">
                    <select
                      value={expectedPayoutType}
                      onChange={(e) =>
                        dispatch(
                          updateV2Field("expectedPayoutType", e.target.value),
                        )
                      }
                      className="w-full h-[30px] appearance-none rounded-[8px] border border-[#E4E7EC] bg-white px-3 pr-8 text-[11px] text-[#344054] outline-none focus:border-[#D0D5DD] focus:ring-1 focus:ring-[#D0D5DD] cursor-pointer"
                    >
                      <option value="Per Month">Per Month</option>
                      <option value="Per Video">Per Video</option>
                    </select>

                    {/* Custom arrow */}
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[#667085]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              {errors.expectedPayout && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.expectedPayout}
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Topics you prefer not to work with
              </span>{" "}
              <span className="text-[#475467]">(optional)</span>
            </p>
            <p className="mt-[2px] text-[10px] tracking-normal">
              This helps us avoid mismatched projects
            </p>

            <div className="flex flex-wrap mt-2 gap-2">
              {["None", "Alcohol", "Gambling", "Adult products"].map((t) => {
                const active =
                  t === "None"
                    ? avoidTopics.length === 0
                    : avoidTopics.includes(t);

                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      if (t === "None")
                        dispatch(updateV2Field("avoidTopics", []));
                      else toggleAvoidTopic(t);
                    }}
                    className={`px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px]
                      ${active ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}`}
                  >
                    {t}
                  </button>
                );
              })}

              {avoidTopics
                .filter(
                  (t) => !["Alcohol", "Gambling", "Adult products"].includes(t),
                )
                .map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleAvoidTopic(t)}
                    className="px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px] border-[#FF6600] bg-[#FFF4EC]"
                  >
                    {t}
                  </button>
                ))}

              <button
                type="button"
                onClick={() => setTopicOtherOpen((p) => !p)}
                className={`px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px] flex items-center gap-1
                  ${topicOtherOpen ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}`}
              >
                <span className="text-[#FF6600] font-bold">+</span>
                Other
              </button>
            </div>

            {topicOtherOpen && (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <input
                    value={topicOtherInput}
                    onChange={(e) => setTopicOtherInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = topicOtherInput.trim();
                        if (val) {
                          toggleAvoidTopic(val);
                          setTopicOtherInput("");
                          setTopicOtherOpen(false);
                        }
                      }
                      if (e.key === "Escape") {
                        setTopicOtherOpen(false);
                        setTopicOtherInput("");
                      }
                    }}
                    placeholder="Type a topic (e.g. Politics)"
                    className="flex-1 border border-[#D8D8DD] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = topicOtherInput.trim();
                      if (val) {
                        toggleAvoidTopic(val);
                        setTopicOtherInput("");
                        setTopicOtherOpen(false);
                      }
                    }}
                    disabled={!topicOtherInput.trim()}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition
                      ${
                        topicOtherInput.trim()
                          ? "bg-[#FF6600] text-white hover:opacity-90"
                          : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
                      }`}
                  >
                    Add
                  </button>
                </div>
                <p className="text-[10px] text-[#667085] mt-1">
                  Tip: press <span className="font-medium">Enter</span> to add
                  quickly.
                </p>
              </div>
            )}
          </div>
        </div>

        {Object.keys(errors).length > 0 && (
          <div role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <p className="font-medium">Please complete these fields to proceed:</p>
            <ul className="mt-2 space-y-1">
              {Object.entries(errors).map(([field, message]) => (
                <li key={field}>
                  <button type="button" className="text-left underline" onClick={() => scrollToFirstError({ [field]: message })}>
                    {fieldLabels[field]}: {message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-[50px] flex items-center justify-end gap-3">
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              dispatch(prevStep());
              setCurrentStep((prev) => prev - 1);
            }}
          >
            <LeftArrowShort />
            Go Back
          </CustomButton>

          {/* <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              setShowSkipToast(true);
              setTimeout(() => {
                dispatch(nextStep());
                setCurrentStep((prev) => prev + 1);
              }, 2000);
            }}
          >
            <Skip />
            Skip
          </CustomButton> */}

          <CustomButton
            backgroundColor="#FF6600"
            fontSize="14px"
            fontWeight="600"
            color="white"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px]"
            onClick={handleProceed}
          >
            Proceed
          </CustomButton>
        </div>
      </div>

      <SkipToast
        visible={showSkipToast}
        onClose={() => setShowSkipToast(false)}
      />
    </>
  );
};

export default ApplicationStep4;
